import { create } from 'zustand'
import { ToneMappingMode } from 'postprocessing'

export type AAMode = 'fxaa' | 'smaa' | 'off'

interface PostProcessingState {
  globalEnabled: boolean

  // Quality
  aaMode: AAMode
  toneMappingEnabled: boolean
  toneMappingMode: ToneMappingMode

  // Atmosphere
  vignetteEnabled: boolean
  vignetteDarkness: number
  vignetteOffset: number
  bloomEnabled: boolean
  bloomIntensity: number
  bloomLuminanceThreshold: number
  bloomLuminanceSmoothing: number

  // FPS (written by monitor hook)
  currentFps: number

  // Persistence
  isDirty: boolean
  isSaving: boolean

  // Actions
  resetToDefaults: () => void
  setGlobalEnabled: (value: boolean) => void
  setAAMode: (value: AAMode) => void
  setToneMappingEnabled: (value: boolean) => void
  setToneMappingMode: (value: ToneMappingMode) => void
  setVignetteEnabled: (value: boolean) => void
  setVignetteDarkness: (value: number) => void
  setVignetteOffset: (value: number) => void
  setBloomEnabled: (value: boolean) => void
  setBloomIntensity: (value: number) => void
  setBloomLuminanceThreshold: (value: number) => void
  setBloomLuminanceSmoothing: (value: number) => void
  setCurrentFps: (value: number) => void
  saveToServer: () => Promise<void>
  loadFromServer: () => Promise<void>
}

const DEFAULTS = {
  globalEnabled: true,

  aaMode: 'fxaa' as AAMode,
  toneMappingEnabled: true,
  toneMappingMode: ToneMappingMode.ACES_FILMIC,

  vignetteEnabled: true,
  vignetteDarkness: 0.35,
  vignetteOffset: 0.5,
  bloomEnabled: true,
  bloomIntensity: 0.3,
  bloomLuminanceThreshold: 0.6,
  bloomLuminanceSmoothing: 0.4,

  currentFps: 60,
}

export const TONE_MAPPING_OPTIONS: { value: ToneMappingMode; label: string }[] = [
  { value: ToneMappingMode.ACES_FILMIC, label: 'ACES Filmic' },
  { value: ToneMappingMode.AGX, label: 'AgX' },
  { value: ToneMappingMode.NEUTRAL, label: 'Neutral' },
  { value: ToneMappingMode.REINHARD, label: 'Reinhard' },
  { value: ToneMappingMode.REINHARD2, label: 'Reinhard 2' },
  { value: ToneMappingMode.REINHARD2_ADAPTIVE, label: 'Reinhard 2 Adaptive' },
  { value: ToneMappingMode.UNCHARTED2, label: 'Uncharted 2' },
  { value: ToneMappingMode.CINEON, label: 'Cineon' },
  { value: ToneMappingMode.LINEAR, label: 'Linear' },
]

export const AA_MODE_OPTIONS: { value: AAMode; label: string }[] = [
  { value: 'fxaa', label: 'FXAA' },
  { value: 'smaa', label: 'SMAA' },
  { value: 'off', label: 'Off' },
]

export const usePostProcessingStore = create<PostProcessingState>((set) => ({
  ...DEFAULTS,
  isDirty: false,
  isSaving: false,

  resetToDefaults: () => set({ ...DEFAULTS, isDirty: true }),

  setGlobalEnabled: (value) => set({ globalEnabled: value, isDirty: true }),
  setAAMode: (value) => set({ aaMode: value, isDirty: true }),
  setToneMappingEnabled: (value) => set({ toneMappingEnabled: value, isDirty: true }),
  setToneMappingMode: (value) => set({ toneMappingMode: value, isDirty: true }),
  setVignetteEnabled: (value) => set({ vignetteEnabled: value, isDirty: true }),
  setVignetteDarkness: (value) => set({ vignetteDarkness: value, isDirty: true }),
  setVignetteOffset: (value) => set({ vignetteOffset: value, isDirty: true }),
  setBloomEnabled: (value) => set({ bloomEnabled: value, isDirty: true }),
  setBloomIntensity: (value) => set({ bloomIntensity: value, isDirty: true }),
  setBloomLuminanceThreshold: (value) => set({ bloomLuminanceThreshold: value, isDirty: true }),
  setBloomLuminanceSmoothing: (value) => set({ bloomLuminanceSmoothing: value, isDirty: true }),
  setCurrentFps: (value) => set({ currentFps: value }),

  saveToServer: async () => {
    set({ isSaving: true })
    console.log('[PostProcessingStore] Save disabled in static mode')
    set({ isDirty: false, isSaving: false })
  },

  loadFromServer: async () => {
    try {
      const res = await fetch('/data/postprocessing-config.json')
      if (res.ok) {
        const data = await res.json()
        if (data && typeof data === 'object') {
          set({ ...data, isDirty: false })
        }
      }
    } catch {
      console.log('[PostProcessingStore] Config not available, using defaults')
    }
  },
}))
